import { Modal } from "@circleco/compass/components/Modal";
import { CompassProvider } from "@circleco/compass/context/CompassProvider";
import { useState } from "react";

import { RichTextViewContent } from "@/react/components/SettingsApp/Onboarding/components/RichTextViewContent";
import { reactQueryPatch } from "@/react/helpers/backendRequestHelpers";
import { internalApi } from "@/react/helpers/urlHelpers";
import { isSameOrigin } from "@/react/helpers/urlOriginHelpers";
import { punditUserInvalidations } from "@/react/lib/queries/punditUser";

interface OnboardingWelcomePopupProps {
  body: string;
  buttonLabel: string;
  buttonUrl?: string;
  communityMemberId: number;
  title: string;
}

export const OnboardingWelcomePopup = ({
  title,
  body,
  buttonLabel,
  buttonUrl,
  communityMemberId,
}: OnboardingWelcomePopupProps) => {
  const [isOpen, setIsOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);

    if (!open) {
      void markWelcomePopupAsSeen();
    }
  };

  const handleButtonClick = async () => {
    if (buttonUrl) {
      if (isSameOrigin(window, buttonUrl)) {
        setIsLoading(true);
        await markWelcomePopupAsSeen();
        window.location.href = buttonUrl;
      } else {
        setIsOpen(false);
        void markWelcomePopupAsSeen();
        window.open(buttonUrl, "_blank", "noopener,noreferrer");
      }
    } else {
      setIsOpen(false);
      void markWelcomePopupAsSeen();
    }
  };

  const markWelcomePopupAsSeen = async () => {
    try {
      await reactQueryPatch(
        internalApi.communityMembers.preference({ id: communityMemberId }),
        { has_seen_welcome_popup: true },
      );
      void punditUserInvalidations.all();
    } catch {
      // We intentionally ignore errors here because we don't want to block the user
    }
  };

  return (
    <CompassProvider>
      <Modal.Root open={isOpen} onOpenChange={handleOpenChange} size="md">
        <Modal.Content>
          <Modal.Header title={title} />
          <Modal.Body>
            <RichTextViewContent value={body} />
          </Modal.Body>
          <Modal.Footer
            layout="full-width"
            primaryAction={{
              label: buttonLabel,
              variant: "branded",
              onClick: () => void handleButtonClick(),
              loading: isLoading,
            }}
          />
        </Modal.Content>
      </Modal.Root>
    </CompassProvider>
  );
};
